import { useQuery } from '@tanstack/react-query';
import { correctionsApi, PENDING_CORRECTION_COUNT_QUERY_KEY } from '../api/attendance.api.js';
import { PENDING_LEAVE_COUNT_QUERY_KEY, leaveRequestsApi } from '../api/leave.api.js';
import { letterQueueQueryKey, lettersApi } from '../api/letters.api.js';
import { flaggedScansApi, FLAGGED_SCAN_COUNT_QUERY_KEY } from '../api/flaggedScans.api.js';
import { missedCheckoutsApi, MISSED_CHECKOUT_COUNT_KEY } from '../api/missedCheckouts.api.js';

/*
 * THE NUMBERS THE RAIL SAYS OUT LOUD.
 *
 * Every hook here returns `number | null`, and `null` means WE DO NOT KNOW —
 * first load, a failed request, or a role/feature that never asks. It never
 * means zero (rule 3.3). The rail is the one place a reader glances rather
 * than reads, so a confident `0` over a failed feed is worse there than
 * anywhere else in the app: it says "nothing to do" about a queue nobody has
 * counted. The renderers turn `null` into an absence or an em-dash; none of
 * them turn it into a digit.
 *
 * THE QUEUE COUNTS ARE SERVER COUNTS (corrections, leave, flagged scans, ERP;
 * letters reads its list's `total`). Each is built server-side from the very
 * predicate its list uses, and keyed under its list's prefix, so:
 *
 *   - the badge counts the rows that queue SHOWS — one `where`, one scope
 *     rule, not a second count with its own idea of a manager's scope
 *     (rules 3.2 and 3.6);
 *   - approving something on the page moves the rail with it, because the
 *     page's prefix invalidation (`['corrections']`, `['leave-requests']`)
 *     covers the count's key too.
 *
 * They used to BE the queue's request — the whole list, downloaded to read its
 * length, on every screen the rail is on. A paged list cannot do that job (it
 * would report the page size), and a COUNT is a fraction of the bytes. Each is
 * given a `staleTime` instead of a polling timer, so walking around the console
 * re-uses the cache rather than re-fetching per navigation. The live headcount
 * is the one that polls, because a live figure that does not move is not one
 * — see `useCheckedInNowCount` (hooks/useCheckedInNowCount.ts).
 */

/** How long a queue count may be re-used across navigations before it re-fetches. */
const QUEUE_STALE_MS = 60_000;

/**
 * Correction requests waiting on a decision, in the viewer's scope.
 *
 * A server COUNT, like the flagged-scan badge below, and no longer the whole
 * queue downloaded to read its length (A-07). It still counts the rows the
 * page lists: the list and the count build one `where` server-side
 * (`pendingCorrectionsWhere`) under one scope rule.
 */
export function usePendingCorrectionCount(
  options: { enabled?: boolean } = {}
): number | null {
  const { data, isError } = useQuery({
    queryKey: PENDING_CORRECTION_COUNT_QUERY_KEY,
    queryFn: correctionsApi.pendingCount,
    staleTime: QUEUE_STALE_MS,
    enabled: options.enabled ?? true,
  });
  if (isError || typeof data !== 'number') return null;
  return data;
}

/**
 * Flagged scans waiting on a decision.
 *
 * The ONE badge here backed by a dedicated server count rather than by
 * `data.filter(...)` over the queue's own rows, and the difference is forced:
 * the queue is PAGED (`FLAGGED_PAGE_SIZE`), so counting its rows would report
 * the page size as the backlog on any tenant with more than a page of them —
 * a confident wrong number, which rule 3.3 rates worse than none. The count
 * endpoint applies the identical status and scope predicates as the list, so
 * the two still answer one question (rule 3.2).
 */
export function usePendingScanCount(options: { enabled?: boolean } = {}): number | null {
  const { data, isError } = useQuery({
    queryKey: FLAGGED_SCAN_COUNT_QUERY_KEY,
    queryFn: () => flaggedScansApi.count(),
    staleTime: QUEUE_STALE_MS,
    enabled: options.enabled ?? true,
  });
  if (isError || typeof data !== 'number') return null;
  return data;
}

/** Past days nobody closed — the missing check-out queue's own predicate, counted. */
export function useMissedCheckoutCount(options: { enabled?: boolean } = {}): number | null {
  const { data, isError } = useQuery({
    queryKey: MISSED_CHECKOUT_COUNT_KEY,
    queryFn: () => missedCheckoutsApi.count(),
    staleTime: QUEUE_STALE_MS,
    enabled: options.enabled ?? true,
  });
  if (isError || typeof data !== 'number') return null;
  return data;
}

/** Leave requests waiting on a decision — the rows /leave/manage lists, as a server COUNT (A-07). */
export function usePendingLeaveCount(
  options: { enabled?: boolean } = {}
): number | null {
  const { data, isError } = useQuery({
    queryKey: PENDING_LEAVE_COUNT_QUERY_KEY,
    queryFn: leaveRequestsApi.pendingCount,
    staleTime: QUEUE_STALE_MS,
    enabled: options.enabled ?? true,
  });
  if (isError || typeof data !== 'number') return null;
  return data;
}

/**
 * Letter requests waiting on a decision — the rows the Letters screen's
 * approvals tab lists, through that tab's own query key, so the two cannot
 * state different numbers.
 *
 * Gated by the caller on the `letters` feature and on whether the reader may
 * decide anything: on a tenant with the module off, or for a role the server
 * would refuse, this must issue no request rather than count a 403 as zero.
 */
export function usePendingLetterCount(
  options: { enabled?: boolean } = {}
): number | null {
  const { data, isError } = useQuery({
    queryKey: letterQueueQueryKey('PENDING'),
    queryFn: () => lettersApi.list({ status: 'PENDING' }),
    staleTime: QUEUE_STALE_MS,
    enabled: options.enabled ?? true,
  });
  if (isError || !data) return null;
  /* `total`, NOT the rows. The query already narrows to PENDING server-side, so
     re-filtering the page did nothing except cap the badge at the endpoint's
     200: a queue of 340 read "200" on the rail, which is the one number whose
     whole job is saying how much work is waiting. */
  return data.total;
}
